import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner, FaTrash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import Select from "react-select";
import dayjs from 'dayjs';
import AsyncSelect from "react-select/async";
import QRCode from "qrcode";


interface SalesOrderForm {
  id?: string;
  sales_order_no: string;
  customer_id: string;
  warehouse_id: string;
  status?: string;
  created_by?: string;
  created_at?: string;
  payment_method?: string;
  subTotal?: number;
  grandTotal?: number;
  tenant?: {
    name?: string;
    address?: string;
    phone?: string;
    notes?: string;
  };
}

interface SalesOrderDetail {
  id?: string;
  productId: string;
  qty: number;
  basePrice?: number;
  discPct?: number;
  discAmount?: number;
  totalPrice?: number;
  createdByUserId?: string;
}

interface Customer {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  base_price: number;
}

const SalesOrdersForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const { user, logout } = useAuth();
  const isFinalStatus = (status: string | undefined) =>  status === 'Complete' || status === 'Void';

  const [formData, setFormData] = useState<SalesOrderForm>({
    sales_order_no: "",
    customer_id: "",
    warehouse_id: "",
  });

  const [orderDetails, setOrderDetails] = useState<SalesOrderDetail[]>([{
    productId: "",
    qty: 0,
    basePrice: 0,
	discPct: 0,
	discAmount: 0,
	totalPrice: 0,
  }]);

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
    
  const [subTotal, setSubTotal] = useState(0);
  const [discPct, setDiscPct] = useState(0);
  const [discAmount, setDiscAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [nextSoNumber, setNextSoNumber] = useState('');
  const [soId, setSoId] = useState<string | undefined>(id); // gunakan id awal jika edit


  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  //useEffect
  useEffect(() => {

  }, [orderDetails, discPct, discAmount]);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
		
	  const updatePricesByCustomer = async () => {
		
		if (orderDetails.length === 0) return;
		if (isFinalStatus(formData.status)) return;
		
		setLoadingPrice(true);

		const updatedDetails = await Promise.all(
		  orderDetails.map(async (item) => {		
			if (!item.productId) return item;

			const price = await fetchPriceForProduct(item.productId, formData.customer_id);
			
			return {
			  ...item,
			  basePrice: price,
			  discPct: item.discPct,
			  discAmount: item.discAmount,
			  totalPrice: ((item.qty || 0) * price) - ((item.qty || 0) * price * discPct ) - (discAmount * (item.qty || 0)),
			};
		  })
		);
		
		setOrderDetails(updatedDetails);
		setLoadingPrice(false);
	  };

	  updatePricesByCustomer();
  }, [formData.customer_id]);

  useEffect(() => {
    //calculateTotals(orderDetails);
  }, [orderDetails, discPct, discAmount]);

  useEffect(() => {
	  if (!isEdit) {
		//axios
		//  .get(`${API}/sales-orders/last-number`, getAuthHeader())
		//  .then((res) => setNextSoNumber(res.data.nextNumber))
		//  .catch((err) => console.error('Failed to fetch SO number', err));
	  }
  }, [isEdit]);
  
  
  //fetchData and process data
  const fetchData = async () => {
    try {
      	  
	  const [custRes, whRes, prodRes] = await Promise.all([
        axios.get(`${API}/customers`, getAuthHeader()),
        axios.get(`${API}/warehouses`, getAuthHeader()),
        axios.get(`${API}/products`, getAuthHeader()),
      ]);

      setCustomers(Array.isArray(custRes.data) ? custRes.data : custRes.data.data || []);
      setWarehouses(Array.isArray(whRes.data) ? whRes.data : whRes.data.data || []);
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : prodRes.data.data || []);

	  
      if (isEdit && id) {
        const res = await axios.get(`${API}/sales-orders/${id}`, getAuthHeader());
		//soId = res.data.id;
		setSoId(res.data.id);
        const data = res.data;
		setFormData({
		  id: data.id,
		  sales_order_no: data.so_number || "",
		  customer_id: data.customer?.id || "",
		  warehouse_id: data.warehouse?.id || "",
		  status: data.status || "",
		  created_by: data.createdByUser?.name || data.created_by || "",
		  created_at: data.so_date ? new Date(data.so_date).toLocaleString() : "",
		  tenant: data.tenant || {},
		  payment_method: data.payment_method || "",
		  //subTotal: Number(data.sub_total) || 0,
		});
		
		// inilah tambahan pentingnya:
		if (data.customer) {
			setSelectedCustomer({
			  value: data.customer.id,
			  label: data.customer.name, // sesuaikan properti yang dipakai di label
			});
		  }
		
		setSubTotal(Number(data.sub_total) || 0);
		setDiscPct(Number(data.disc_pct) || 0);
		setDiscAmount(Number(data.disc_amount) || 0);
        
		setOrderDetails(
		  Array.isArray(data.details)
			? data.details.map((d: any) => ({
				id: d.id,
				productId: d.product?.id || '',
				qty: isNaN(Number(d.qty)) ? 0 : Number(d.qty),
				basePrice: isNaN(Number(d.base_price)) ? 0 : Number(d.base_price),
				discPct: isNaN(Number(d.disc_pct)) ? 0 : Number(d.disc_pct),
				discAmount: isNaN(Number(d.disc_amount)) ? 0 : Number(d.disc_amount),
				totalPrice: isNaN(Number(d.total_price)) ? 0 : Number(d.total_price),
			  }))
			: []
		);
		

      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

	if (name !== "discPct" && name !== "discAmount") {
		calculateTotals(orderDetails);
	}
  };
 
  const handleDetailChange = async (index: number, field: string, value: any) => {
	  const updated = [...orderDetails];
	  updated[index] = { ...updated[index], [field]: value };

	  if (field === "productId" && value) {
		try {
		  const res = await axios.get(`${API}/products-price/lookup`, {
			params: {
			  productId: value,
			  customerId: formData.customer_id || undefined,
			},
			headers: getAuthHeader().headers,
		  });

		  const price = Number(res.data.amount ?? 0);
		  updated[index].basePrice = isNaN(price) ? 0 : price;
		  
		  updated[index].discPct = updated[index].discPct ?? 0;
		  updated[index].discAmount = updated[index].discAmount ?? 0;
		  
		  updated[index].totalPrice = (updated[index].qty * price) - (updated[index].qty * price * (updated[index].discPct ?? 0)) - ((updated[index].discAmount ?? 0) * updated[index].qty);
		  //updated[index].totalPrice = updated[index].qty * price;
		} catch (error) {
		  //console.error("Gagal ambil harga:", error);
		  updated[index].basePrice = 0;
		  updated[index].discPct = 0;
		  updated[index].discAmount = 0;
		  updated[index].totalPrice = 0;
		}
	  }

	  if (field === "qty" || field === "discPct" || field === "discAmount") {
		
		const qty = Number(value);
		const price = updated[index].basePrice ?? 0;
		
		const discPct = Math.min(Math.max(updated[index].discPct ?? 0, 0), 100);
		const discAmount = updated[index].discAmount ?? 0;
		
		//updated[index].discPct = updated[index].discPct ?? 0;
		//updated[index].discAmount = updated[index].discAmount ?? 0;
		  
		updated[index].totalPrice = (updated[index].qty * price) - (updated[index].qty * price * (updated[index].discPct ?? 0) / 100) - ((updated[index].discAmount ?? 0) * updated[index].qty);
		//updated[index].totalPrice = qty * price;
	  }
 
	  setOrderDetails(updated);
	  calculateTotals(updated);
	};

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    const validDetails = orderDetails.filter(
      (d) => d.productId && d.qty > 0
    );

    if (validDetails.length === 0) {
      alert("Please add at least one valid product with quantity.");
      return;
    }
	
    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    try {
	  const newSubTotal = subTotal;
	  const validDiscPct = Math.min(Math.max(discPct, 0), 100);
	  const validDiscAmount = Math.min(discAmount, newSubTotal);
	  
	  
      const payload = {
		  tenantId: user.tenantId,
		  customerId: formData.customer_id || null,
		  warehouseId: formData.warehouse_id,
		  soNumber: formData.sales_order_no,
		  soDate: new Date().toISOString(),
		  status: formData.status || 'Draft',
		  discPct,
		  discAmount,
		  subTotal,
		  grandTotal: Math.max(0, newSubTotal - (newSubTotal * validDiscPct / 100) - validDiscAmount),
		  paymentMethod: formData.payment_method || "",
		  details: orderDetails.map((d) => ({
			productId: d.productId,
			qty: Number(d.qty),
			basePrice: Number(d.basePrice),
			discPct: Number(d.discPct),
			discAmount: Number(d.discAmount),
			totalPrice: Number(d.totalPrice),
			createdByUserId: user.id,
		  })),
		};
	
	  //console.log("Payload yang dikirim:", payload);

      if (isEdit && id) {
	    await axios.put(`${API}/sales-orders/${id}`, payload, getAuthHeader());
        alert("Sales Order updated!");
      } else {
        await axios.post(`${API}/sales-orders`, payload, getAuthHeader());
        alert("Sales Order created!");
      }

      navigate("/sales-orders");
    } catch (error) {
      //console.error("Failed to save sales order:", error);
      alert("Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
	const handleComplete = async () => {
	  const user = JSON.parse(localStorage.getItem("user") || "{}");
	  if (!user?.id || !user?.tenantId) {
		alert("User session expired. Please re-login.");
		return;
	  }
		
	  //console.log(subTotal);
	  
	  const validDetails = orderDetails.filter((d) => d.productId && d.qty > 0);
	  if (validDetails.length === 0) {
		alert("Please add at least one valid product with quantity.");
		return;
	  }

	  const hasZeroPrice = validDetails.some((d) => Number(d.basePrice) === 0);
	  if (hasZeroPrice) {
		const confirmZero = window.confirm(
		  "Some products have a base price of 0. Are you sure you want to save and complete this order?"
		);
		if (!confirmZero) return;
	  }

	  if (!window.confirm("Do you want to save and complete this order?")) return;

	  setLoading(true);
	  try {
		let soIdFinal = soId;
		const newSubTotal = subTotal;
		const validDiscPct = Math.min(Math.max(discPct, 0), 100);
		const validDiscAmount = Math.min(discAmount, newSubTotal);

		const payload = {
		  tenantId: user.tenantId,
		  customerId: formData.customer_id?.trim() || null,
		  warehouseId: formData.warehouse_id?.trim() || null,
		  soNumber: formData.sales_order_no,
		  soDate: new Date().toISOString(),
		  status: 'Complete',
		  discPct,
		  discAmount,
		  subTotal,
		  grandTotal: Math.max(0, newSubTotal - (newSubTotal * validDiscPct / 100) - validDiscAmount),
		  paymentMethod: formData.payment_method || '',
		  details: orderDetails.map((d) => ({
			productId: d.productId,
			qty: Number(d.qty),
			basePrice: Number(d.basePrice),
			discPct: Number(d.discPct),
			discAmount: Number(d.discAmount),
			totalPrice: Number(d.totalPrice),
		  })),
		};

		//console.log(payload);
		if (!soIdFinal) {
		  const res = await axios.post(`${API}/sales-orders`, payload, getAuthHeader());
		  soIdFinal = res.data;
		} else {
		  await axios.put(`${API}/sales-orders/${soIdFinal}`, payload, getAuthHeader());
		}

		alert("Sales Order Completed!");
		await fetchData();
		setSoId(soIdFinal);
		
		// ✅ konfirmasi print
		const doPrint = window.confirm("Do you want to print the receipt?");
		if (doPrint && soIdFinal) {
		  const printWindow = window.open('', '_blank');
		  if (printWindow) {
			await handlePrint(soIdFinal, printWindow);
		  }
		}

		navigate("/sales-orders");
	  } catch (err) {
		alert("Failed to complete Sales Order. Please check the console.");
	  } finally {
		setLoading(false);
	  }
	};


  const handleVoid = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    await axios.patch(`${API}/sales-orders/${formData.id}/setVoid`, {}, getAuthHeader());
      navigate("/sales-orders");
	alert("Sales Order Voided!");
	setLoading(false);
  };

  
  //detail process
  const addDetailRow = () => {
    setOrderDetails([...orderDetails, {
      productId: "",
      qty: 0,
      basePrice: 0,
	  discPct: 0,
	  discAmount: 0,
	  totalPrice: 0,
    }]);
  };

  const removeDetailRow = (index: number) => {
    if (orderDetails.length === 1) return;
    setOrderDetails(orderDetails.filter((_, i) => i !== index));
  };
  
  
  //additional process
  const formatNumber = (value: string | number) => {
	  const num = Number(value);
	  return isNaN(num) ? "0" : num.toLocaleString("id-ID");
	};
  
  const isDisabled = ["Complete", "Void"].includes(formData.status || "");

  const fetchPriceForProduct = async (productId: string, customerId?: string) => {
	  try {
		const res = await axios.get(`${API}/products-price/lookup`, {
		  params: {
			  productId,
			  ...(customerId ? { customerId } : {}),
			},
		  headers: getAuthHeader().headers,
		});
		return Number(res.data.amount ?? 0);
	  } catch (err) {
		//console.warn("Failed to fetch price for product:", productId, err);
		return 0;
	  }
	};

  const calculateTotals = (details: SalesOrderDetail[]) => {
	  const newSubTotal = details.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
	  const validDiscPct = Math.min(Math.max(discPct, 0), 100);
	  const validDiscAmount = Math.min(discAmount, newSubTotal);
	  const newGrandTotal = Math.max(0, newSubTotal - (newSubTotal * validDiscPct / 100) - validDiscAmount);
	  
	  setSubTotal(newSubTotal);
	  setGrandTotal(newGrandTotal);
	  
	};

  const loadCustomerOptions = async (inputValue: string) => {
	  try {
		const user = JSON.parse(localStorage.getItem("user") || "{}");
		const res = await axios.get(`${API}/customers/lookup`, {
		  ...getAuthHeader(),
		  params: {
			search: inputValue,
			limit: 10000,
			tenantId: user?.tenantId || "",
		  },
		});
		const items = res.data || [];
		return items.map((cust: any) => ({
		  label: cust.name,
		  value: cust.id,
		}));
	  } catch (error) {
		//console.error("Gagal load customer options", error);
		return [];
	  }
	};
	
  const generateQrDataUrl = async (text: string): Promise<string> => {
	  try {
		return await QRCode.toDataURL(text);
	  } catch (err) {
		//console.error("Failed to generate QR code:", err);
		return "";
	  }
	};
	
  const handlePrint = async (soId: string, printWindow: Window): Promise<void> => {
	  //return new Promise((resolve) => {
		//const printWindow = window.open('', '_blank');

		if (!printWindow) {
		  alert("Popup blocked! Izinkan pop-up untuk mencetak.");
		  //return resolve();
		  return;
		}

		const formatNumber = (value: number) =>
		  isNaN(value) ? "0" : value.toLocaleString("id-ID");

		const mergedItems = orderDetails.reduce((acc, item) => {
		  const key = `${item.productId}-${item.basePrice}`;
		  if (!acc[key]) {
			acc[key] = { ...item };
		  } else {
			acc[key].qty += Number(item.qty);
			acc[key].totalPrice = (acc[key].totalPrice || 0) + Number(item.totalPrice ?? 0);
		  }
		  return acc;
		}, {} as Record<string, SalesOrderDetail>);
		
		const res = await axios.get(`${API}/sales-orders/${soId}`, getAuthHeader());
		const data = res.data;
		//console.log(res.data);
		const qrDataUrl = await generateQrDataUrl(data.sales_order_no || "0");
		
		//console.log("123",formData.sales_order_no);
		const html = `
		  <html>
			<head>
			  <title>Sales Order Receipt</title>
			  <style>
				body { font-family: monospace; font-size: 12px; padding: 8px; }
				.text-right { text-align: right; }
				.text-center { text-align: center; }
				.border-line { border-top: 1px dashed #000; margin: 8px 0; }
				table { width: 100%; border-collapse: collapse; }
				td { vertical-align: top; }
			  </style>
			</head>
			<body>
			  <div class="text-center">
				<strong>${data.tenant?.name || '-'}</strong><br/>
				<strong>${data.tenant?.address || '-'}</strong><br/>
				<strong>${data.tenant?.phone || '-'}</strong><br/>
			  </div>
			  <div class="border-line"></div>
			  <table>
				<tr>
				  <td style="width:50%;">
					<strong>${dayjs(data.created_at).format('DD/MM/YYYY - HH:mm')}</strong><br/>
					<strong>SO No: ${data.so_number}</strong><br/>
					<strong>Payment: ${data.payment_method || '-' }</strong><br/>
				  </td>
				  <td style="width:50%;">
					<strong>Customer: ${data.customer.name || "-"}</strong><br/>
					<strong>Kasir: ${data.createdByUser.name || "-"}</strong><br/>
				  </td>
				</tr>
			  </table>
			  <div class="border-line"></div>
			  ${Object.values(mergedItems).map((item) => {
				const productName = products.find(p => p.id === item.productId)?.name || '';
				const price = Number(item.basePrice || 0);
				const discAmt = Number(item.discAmount || 0);
				const total = Number((item.totalPrice || item.qty * price) - 0);
				return `
				  <div style="display:flex; justify-content:space-between;">
					<div><strong>${item.qty}x ${productName}</strong></div>
					<div><strong>Rp.${formatNumber(total)}</strong></div>
				  </div>
				  <div style="font-size:12px; color:#000;"><strong>@ Rp.${formatNumber(price)}</strong></div>
				  ${discAmt > 0 ? `
					  <div style="font-size:12px;">
						<strong>- Diskon: Rp.${formatNumber(discAmt * item.qty)}</strong>
					  </div>
					` : ''}
				`;
			  }).join('')}
			  <div class="border-line"></div>
			  <table>
				<tr>
				  <td><strong>Subtotal</strong></td>
				  <td class="text-right"><strong>Rp.${formatNumber(subTotal)}</strong></td>
				</tr>
				${(discPct > 0 || discAmount > 0) ? `
				  <tr>
					<td><strong>Diskon</strong></td>
					<td class="text-right"><strong>-Rp.${formatNumber((subTotal * discPct / 100) + discAmount)}</strong></td>
				  </tr>
				` : ''}
				<tr>
				  <td><strong>Total</strong></td>
				  <td class="text-right"><strong>Rp.${formatNumber(subTotal- (subTotal * discPct/100) - discAmount)}</strong></td>
				</tr>
			  </table>
			  <div class="border-line"></div>
			  <div class="text-center">
				${data.tenant?.notes || '-'}
			  </div>
			  <div class="text-center">
				<img src="${qrDataUrl}" alt="QR Code" width="110" height="110" />
			  </div>
			  
			  <script>
				window.onload = function() {
				  window.focus();
				  window.print();
				  setTimeout(function() { window.close(); }, 500);
				};
			  </script>
			</body>
		  </html>
		`;

		// 👉 Jangan panggil print langsung di sini!
		printWindow.document.open();
		printWindow.document.write(html);
		printWindow.document.close();

		// Tunggu 1.5 detik sebagai fallback kalau onload gagal
		//setTimeout(() => resolve(), 2000);
		setTimeout(() => {
		  printWindow?.close();
		}, 2000);
	  
	};

  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-blue-600">
        <FaSpinner className="animate-spin mr-2" /> Loading...
      </div>
    );
  }

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? "Edit Sales Order" : "Add Sales Order"}
          </h2>
          <div className="flex gap-2">
		    {formData.status === "Complete" && soId && (
			  <button
				onClick={() => {
				  const win = window.open('', '_blank');
				  if (win && soId) {
					handlePrint(soId, win);
				  }
				}}
				type="button"
				className="bg-green-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
			  >
				🖨️ Print
			  </button>
			)}
            {formData.status != "Complete" && formData.status != "Void" && (
              <button
				onClick={handleComplete}
                type="button"
                className="bg-yellow-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
				disabled={loading}
              >
                Complete SO
              </button>
            )}
            {formData.status === "Complete" && (
              <>
                <button
                  onClick={handleVoid}
                  type="button"
                  className="bg-red-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
                >
                  Void
                </button>
              </>
            )}
			{formData.status != "Void" && formData.status != "Complete" && (
              <button
              type="submit"
              form="sales-form"
              className="bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />} Save
            </button>
            )}
            
            <button
              onClick={() => navigate("/sales-orders")}
              className="bg-gray-300 text-gray-800 px-3 py-1 rounded flex items-center gap-1 text-[13px]"
            >
              <FaTimes /> Cancel
            </button>
          </div>
        </div>

        <form id="sales-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Sales Order No</label>
              <input
                type="text"
                name="sales_order_no"
                value={formData.sales_order_no || ""}
                onChange={handleChange}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
                disabled
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Customer</label>
              <AsyncSelect
				  cacheOptions
				  defaultOptions
				  isDisabled={isDisabled}
				  loadOptions={loadCustomerOptions}
				  value={selectedCustomer}
				  onChange={(selected) => {
					setSelectedCustomer(selected);
					setFormData(prev => ({
					  ...prev,
					  customer_id: selected?.value || "",
					}));
				  }}
				  placeholder="-- Search Customer --"
				  isClearable
				  className="flex-1 text-[13px]"
				  styles={{
					control: (base) => ({ ...base, minHeight: "36px" }),
					menu: (base) => ({ ...base, zIndex: 9999 }),
				  }}
				/>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Warehouse <span>*</span></label>
              <Select
				  className="flex-1 text-[13px]"
				  options={warehouses.map((w) => ({
					value: w.id,
					label: w.name,
				  }))}
				  value={warehouses
					.map((w) => ({ value: w.id, label: w.name }))
					.find((opt) => opt.value === formData.warehouse_id)}
				  onChange={(selected) =>
					setFormData({ ...formData, warehouse_id: selected?.value || "" })
				  }
				  isDisabled={isDisabled}
				  isClearable
				  placeholder="-- Select Warehouse --"
				  menuPortalTarget={document.body}
				  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
				  required
				/>

            </div>
			<div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Payment Method</label>
              <input
                type="text"
                name="payment_method"
                value={formData.payment_method || ""}
                onChange={handleChange}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
                disabled={isDisabled}
              />
            </div>
            {[{ label: "Status", value: formData.status },
              { label: "Created By", value: formData.created_by },
              { label: "Created At", value: formData.created_at ? dayjs(formData.created_at).format("DD/MM/YYYY HH:mm") : "" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <label className="w-36 text-[13px] font-medium">{item.label}</label>
                <input
                  type="text"
                  value={item.value || ""}
                  disabled
                  className="flex-1 border rounded px-2 py-1 text-[13px] bg-gray-100"
                />
              </div>
            ))}
          </div>
		  
		  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
			  {/* Subtotal (readonly, format angka) */}
			  <div>
				<label className="block text-[13px] font-semibold mb-1">Subtotal</label>
				<input
				  type="text"
				  value={formatNumber(subTotal)}
				  readOnly
				  className="w-full border rounded px-2 py-1.5 bg-gray-100 text-right text-[13px]"
				/>
			  </div>

			  {/* Diskon Persen */}
			  <div>
				<label className="block text-[13px] font-semibold mb-1">Disc (%)</label>
				<input
				  type="number"
				  step="0.01"
				  min={0}
				  max={100}
				  value={discPct}
				  onChange={(e) =>
					setDiscPct(Math.min(Math.max(Number(e.target.value), 0), 100))
				  }
				  className="w-full border rounded px-2 py-1.5 text-right text-[13px]"
				  disabled={isDisabled}
				/>
			  </div>

			  {/* Diskon Nominal */}
			  <div>
				<label className="block text-[13px] font-semibold mb-1">Disc Amount</label>
				<input
				  type="number"
				  step="0.001"
				  min={0}
				  max={subTotal}
				  value={discAmount}
				  onChange={(e) =>
					setDiscAmount(Math.min(Math.max(Number(e.target.value), 0), subTotal))
				  }
				  className="w-full border rounded px-2 py-1.5 text-right text-[13px]"
				  disabled={isDisabled}
				/>
			  </div>

			  {/* Grand Total (readonly, dihitung otomatis) */}
			  <div>
				<label className="block text-[13px] font-semibold mb-1">Grand Total</label>
				<input
				  type="text"
				  readOnly
				  value={formatNumber(
					Math.max(0, subTotal - (subTotal * discPct) / 100 - discAmount)
				  )}
				  className="w-full border rounded px-2 py-1.5 bg-gray-100 text-right text-[13px] font-semibold"

				/>
			  </div>
			</div>


		  {loadingPrice && (
			  <div className="text-blue-600 text-[13px] mb-2 flex items-center gap-2">
				<FaSpinner className="animate-spin" /> Updating prices by customer...
			  </div>
			)
		  }


          <div className="mt-6">
            <h3 className="font-semibold mb-2">Sales Order Details</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px] border">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="border px-2 py-1 w-10 text-center">#</th>
                    <th className="border px-2 py-1 w-60 text-center">Product</th>
                    <th className="border px-2 py-1 text-center">Qty</th>
                    <th className="border px-2 py-1 text-center">Price</th>
					<th className="border px-2 py-1 text-center">Disc/Qty</th>
                    <th className="border px-2 py-1 text-center">Total</th>
                    <th className="border px-2 py-1 w-10 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.map((detail, index) => (
                    <tr key={index}>
                      <td className="border px-2 py-1 text-center">{index + 1}</td>
                      <td className="border px-2 py-1">
                        <Select
                          className="text-[13px]"
                          options={products.map((prod) => ({
                            value: prod.id,
                            label: prod.name,
                          }))}
                          value={products.map((prod) => ({ value: prod.id, label: prod.name })).find((opt) => opt.value === detail.productId)}
                          onChange={(selected) => handleDetailChange(index, "productId", selected?.value || "")}
                          isDisabled={isDisabled}
                          menuPortalTarget={document.body}
						  menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </td>
                      <td className="border px-2 py-1 w-40">
                        <input
                          type="number"
                          min="0"
                          className="w-full border rounded px-2 py-1 text-right"
                          value={formatNumber(detail.qty)}
                          onChange={(e) => handleDetailChange(index, "qty", Number(e.target.value))}

                          disabled={isDisabled}
                        />
                      </td>
                      <td className="border px-2 py-1 text-right">
                        {formatNumber(detail.basePrice || 0)}
                      </td>

					  <td className="border px-2 py-1 text-right w-28">
						  <input
							type="number"
							step="0.01"
							min={0}
							className="w-full border rounded px-2 py-1 text-right"
							value={detail.discAmount || 0}
							onChange={(e) =>
							  handleDetailChange(index, "discAmount", Number(e.target.value))
							}
							disabled={isDisabled}
						  />
					  </td>

					  
                      <td className="border px-2 py-1 text-right">
                        {formatNumber(((detail.qty || 0) * (detail.basePrice || 0)) - (((detail.qty || 0) * (detail.basePrice || 0) * (detail.discPct || 0) / 100)) - ((detail.discAmount || 0) * (detail.qty || 0)))}    
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {formData.status != "Void" && formData.status != "Complete" && (
						  <button
                            className="text-blue-600 hover:text-blue-800 mr-2"
                            title="Delete Item"
                            onClick={() => removeDetailRow(index)}
                            disabled={orderDetails.length === 1 || isDisabled}
                          >
                            <FaTrash />
                          </button>
						)}
						
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isDisabled && (
              <button
                type="button"
                onClick={addDetailRow}
                className="mt-2 bg-green-600 text-white px-3 py-1 rounded text-[13px]"
              >
                + Add Product
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesOrdersForm;
